<template>
  <section
    v-if="isVisible"
    class="p-the-a01-earn-point"
    :style="{ '--site-light-color': lightColorCode }"
    aria-labelledby="earn-point-title"
  >
    <div class="c-common-inner">
      <div class="p-the-a01-earn-point__intro">
        <h2 id="earn-point-title" class="p-the-a01-earn-point__title">
          <span data-translate-skip>{{ pointName }}</span>を獲得する方法
        </h2>
        <p class="p-the-a01-earn-point__description">
          ポイントは、組織ごとに定められた条件に基づいて付与されます。どのような条件やタイミングで付与されるのか、付与基準をご確認ください。
        </p>
      </div>
      <div class="p-the-a01-earn-point__examples">
        <h3 class="p-the-a01-earn-point__examples-title">付与基準例</h3>
        <div class="p-the-a01-earn-point__list">
          <div
            v-for="condition in conditions"
            :key="condition.id"
            class="p-the-a01-earn-point__item"
          >
            <div class="p-the-a01-earn-point__icon">
              <IconInvestedRounded
                :type="iconType(condition)"
                :icon-size="36"
                :bg-color="colorCode"
                icon-color="#ffffff"
              />
            </div>
            <div class="p-the-a01-earn-point__item-body">
              <span
                class="p-the-a01-earn-point__name"
                data-translate-skip
              >
                {{ condition.name }}
              </span>
              <div
                class="p-the-a01-earn-point__point"
                data-translate-skip
              >
                <span class="p-the-a01-earn-point__point__sign">+</span>
                <div class="p-the-a01-earn-point__point__body">
                  <span class="p-the-a01-earn-point__point__value">
                    {{ addComma(condition.points) }}
                  </span>
                  <span class="p-the-a01-earn-point__point__unit">
                    {{ pointName }}
                  </span>
                </div>
              </div>
              <p
                v-if="condition.text"
                class="p-the-a01-earn-point__text"
                data-translate-skip
              >
                {{ condition.text }}
              </p>
            </div>
          </div>
        </div>
        <div
          v-if="showMore"
          class="p-the-a01-earn-point__footer"
        >
          <a :href="urlGrantCriteria" class="p-the-a01-earn-point__more">
            <span class="p-the-a01-earn-point__more__label">
              付与基準をもっと見る
            </span>
            <IconBase
              class="p-the-a01-earn-point__more__icon"
              view-box="0 0 24 24"
              :width="24"
              :height="24"
              :icon-color="colorCode"
            >
              <IconArrowRight />
            </IconBase>
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import { mapState } from "vuex";
import IconBase from "@/common/components/atoms/IconBase.vue";
import IconArrowRight from "@/common/components/icons/IconArrowRight.vue";
import IconInvestedRounded from "@/common/components/icons/IconInvestedRounded.vue";
import { addComma as addCommaFilter } from "@/common/assets/js/filters";
import commons from "@/common/store/modules/commons";
import CONST_DATA from "@/general/mixins/const/constData";
import { GrantCriteria } from "@/general/store/models";

@Options({
  name: "TheA01EarnPointPanel",
  props: {
    conditions: { type: Array, required: true },
    grantCriteriaCount: { type: Number, required: true }
  },
  components: {
    IconBase,
    IconArrowRight,
    IconInvestedRounded
  },
  computed: {
    ...mapState({
      storeColorCode: () => commons.colorCode,
      storeLightColorCode: () => commons.lightColorCode
    })
  }
})
export default class TheA01EarnPointPanel extends Vue {
  conditions!: Array<GrantCriteria>;
  grantCriteriaCount!: number;
  storeColorCode!: string;
  storeLightColorCode!: string;

  get isGroupBudget() {
    return commons?.userInfo.groupBudgetMode;
  }

  get colorCode() {
    if (this.isGroupBudget) {
      return "#fc9014";
    }
    return this.storeColorCode;
  }

  get lightColorCode() {
    if (this.isGroupBudget) {
      return "#FFF5E8";
    }
    return this.storeLightColorCode;
  }

  get pointName() {
    return commons.OrgPageSpecific?.point_name || commons?.unit?.point;
  }

  get urlGrantCriteria() {
    return CONST_DATA.SITE_MAP.GRANT_CRITERIA;
  }

  get isVisible() {
    return this.grantCriteriaCount > 0;
  }

  get showMore() {
    return this.grantCriteriaCount >= 4;
  }

  iconType(condition: GrantCriteria) {
    return condition.grant_criteria_icon_id || 1;
  }

  addComma = addCommaFilter;
}
</script>

<style lang="scss" scoped>
.p-the-a01-earn-point {
  width: 100%;
  padding: rem(64) 0;
  color: #1f2021;
  font-family: $font--noto-sans-only;

  .c-common-inner {
    display: flex;
    flex-direction: column;
    gap: rem(40);
    background-color: transparent;
  }

  &__intro {
    display: flex;
    flex-direction: column;
    gap: rem(24);
    width: 100%;
  }

  &__title {
    margin: 0;
    color: #1f2021;
    font-size: rem(36);
    font-weight: 700;
    line-height: 1.25;
    text-align: center;
    font-feature-settings: "pwid" 1, "palt" 1;
  }

  &__description {
    margin: 0;
    color: #1f2021;
    font-size: rem(16);
    font-weight: 400;
    line-height: 1.8;
    text-align: center;
    overflow-wrap: anywhere;
    font-feature-settings: "palt" 1, "pwid" 1;
  }

  &__examples {
    display: flex;
    flex-direction: column;
    gap: rem(24);
    width: 100%;
  }

  &__examples-title {
    margin: 0;
    color: #1f2021;
    font-size: rem(20);
    font-weight: 700;
    line-height: 1.25;
    text-align: center;
    font-feature-settings: "pwid" 1, "palt" 1;
  }

  &__list {
    display: flex;
    gap: rem(24);
    align-items: stretch;
    width: 100%;
  }

  &__item {
    box-sizing: border-box;
    display: flex;
    flex: 1 1 0;
    flex-direction: row;
    gap: rem(16);
    align-items: flex-start;
    min-width: 0;
    padding: rem(24);
    background-color: var(--site-light-color);
  }

  &__icon {
    display: flex;
    flex: 0 0 rem(36);
  }

  &__item-body {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    gap: rem(8);
    align-items: flex-start;
    min-width: 0;
    width: 100%;
  }

  &__name {
    color: #1f2021;
    font-size: rem(18);
    font-weight: 700;
    line-height: 1.25;
    text-align: left;
    overflow-wrap: anywhere;
    font-feature-settings: "pwid" 1, "palt" 1;
  }

  &__point {
    display: flex;
    gap: rem(4);
    align-items: center;
    justify-content: flex-start;
    color: #da291c;

    &__sign {
      flex-shrink: 0;
      font-size: rem(16);
      font-weight: 400;
      line-height: 1;
    }

    &__body {
      display: flex;
      gap: rem(2);
      align-items: flex-end;
      white-space: nowrap;
    }

    &__value {
      font-size: rem(18);
      font-weight: 700;
      line-height: 1;
      font-feature-settings: "palt" 1, "pwid" 1;
    }

    &__unit {
      font-size: rem(12);
      font-weight: 700;
      line-height: 1;
      font-feature-settings: "palt" 1, "pwid" 1;
    }
  }

  &__text {
    width: 100%;
    margin: 0;
    color: #1f2021;
    font-size: rem(14);
    font-weight: 400;
    line-height: 1.8;
    text-align: left;
    white-space: pre-line;
    overflow-wrap: anywhere;
    font-feature-settings: "palt" 1, "pwid" 1;
  }

  &__footer {
    display: flex;
    justify-content: center;
  }

  &__more {
    display: flex;
    gap: rem(4);
    align-items: center;
    justify-content: center;
    min-width: rem(188);
    height: rem(36);
    padding: 0 rem(8);
    color: #1f2021;
    background: $common-white;
    border: px(2) solid #1f2021;
    border-radius: rem(4);

    &__label {
      font-size: rem(16);
      font-weight: 700;
      line-height: 1.25;
      text-align: center;
      white-space: nowrap;
      font-feature-settings: "pwid" 1, "palt" 1;
    }

    &__icon {
      flex-shrink: 0;
    }
  }

  @include large-sp {
    padding: rem(32) 0;

    .c-common-inner {
      gap: rem(24);
    }

    &__description {
      text-align: left;
    }

    &__examples {
      gap: rem(16);
    }

    &__list {
      flex-direction: column;
      gap: rem(16);
    }

    &__item {
      flex: none;
      width: 100%;
      padding: rem(16);
    }
  }

  @include sp {
    &__title {
      font-size: rem(24);
    }

    &__description {
      font-size: rem(14);
    }

    &__examples-title,
    &__name,
    &__point__value {
      font-size: rem(16);
    }

    &__text {
      font-size: rem(12);
    }

    &__point__unit {
      font-size: rem(10);
    }

    &__more {
      min-width: rem(169);
      height: rem(34);

      &__label {
        font-size: rem(14);
      }
    }
  }
}
</style>
